import React, { useEffect, useMemo, useRef } from "react";
import { Autocomplete, TextField, Box, Typography, Chip, CircularProgress } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import {
  cityPostbackValue,
  filterCityOptions,
  findCityByPostback,
  formatCityLabel,
} from "./bpeCityDisplay";
import { bpeGuideInputSx } from "./bpeWizardLayout";

/**
 * Autocomplete de cidade BPe com busca por nome/UF (listas grandes).
 */
const BPeCityAutocomplete = ({
  id,
  label,
  options = [],
  valuePostback = "",
  onChange,
  disabled = false,
  loading = false,
  placeholder,
  noOptionsText,
  helperText,
  highlight = false,
  autoFocus = false,
}) => {
  const selected = useMemo(
    () => findCityByPostback(options, valuePostback),
    [options, valuePostback]
  );

  const inputRef = useRef(null);
  const focusedForRef = useRef(false);
  // Foca o campo quando ele se torna o "próximo passo" (reduz cliques do atendente).
  useEffect(() => {
    if (autoFocus && !disabled && !loading) {
      if (!focusedForRef.current) {
        focusedForRef.current = true;
        const tid = setTimeout(() => inputRef.current?.focus(), 80);
        return () => clearTimeout(tid);
      }
    } else {
      focusedForRef.current = false;
    }
    return undefined;
  }, [autoFocus, disabled, loading]);

  return (
    <Autocomplete
      id={id}
      fullWidth
      disabled={disabled || loading}
      loading={loading}
      sx={highlight ? bpeGuideInputSx : undefined}
      options={options}
      value={selected}
      openOnFocus
      autoHighlight
      clearOnBlur={false}
      blurOnSelect
      isOptionEqualToValue={(a, b) => cityPostbackValue(a) === cityPostbackValue(b)}
      getOptionLabel={(opt) => formatCityLabel(opt)}
      filterOptions={(opts, state) => filterCityOptions(opts, state.inputValue)}
      noOptionsText={noOptionsText}
      onChange={(_e, city) => onChange(city ? cityPostbackValue(city) : "")}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        const uf = String(option.uf || "").trim();
        return (
          <Box component="li" key={key} {...rest} sx={{ py: 1, px: 1.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} width="100%">
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                {formatCityLabel(option)}
              </Typography>
              {uf ? (
                <Chip label={uf} size="small" variant="outlined" sx={{ height: 22, fontSize: "0.7rem" }} />
              ) : null}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          inputRef={inputRef}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <LocationOnOutlinedIcon fontSize="small" color="action" sx={{ ml: 0.5, mr: -0.25 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      ListboxProps={{
        style: { maxHeight: 280 },
      }}
      slotProps={{
        paper: {
          elevation: 8,
          sx: { mt: 0.5 },
        },
        popper: {
          placement: "bottom-start",
        },
      }}
    />
  );
};

export default BPeCityAutocomplete;
